import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  QRCodeIcon,
  TabBarHomeIcon,
  TabBarPayAtPumpIcon,
  TabBarOrderAheadFoodIcon,
  TabBarOrderAheadDeliveryIcon,
  TabBarOrderAheadMerchIcon,
} from '@ocx/ui-icons';
import { OrderAheadConfigurationNavbarIcon } from '@ocx/graphql';
import { useOrderAheadNavigationOnClick } from '@ocx/feat-order-ahead';
import { useStartPayAtPump } from './useStartPayAtPump';
import { useStartPayInside } from './useStartPayInside';
import { useConfiguration } from '../configuration/useConfiguration';
import { BottomNavigationTab } from '../../components/navigation/BottomNavigation';
import { messages } from './messages';
import { useGetNavigationActions } from '../../hooks/useGetNavigationActions';
import { AppRoutePath } from '../../pages/AppRoutePath';
import { SurveysTabBarIconWithBadgeController } from './SurveysTabBarIconWithBadge.controller';

export const orderAheadIconMap: Record<OrderAheadConfigurationNavbarIcon, React.ComponentType> = {
  [OrderAheadConfigurationNavbarIcon.Food]: TabBarOrderAheadFoodIcon,
  [OrderAheadConfigurationNavbarIcon.Delivery]: TabBarOrderAheadDeliveryIcon,
  [OrderAheadConfigurationNavbarIcon.Merch]: TabBarOrderAheadMerchIcon,
};

export type UseBottomNavigationController = (params: { hasEmail: boolean }) => {
  tabs: BottomNavigationTab[];
};

export const useBottomNavigationController: UseBottomNavigationController = ({ hasEmail }) => {
  const intl = useIntl();
  const {
    config: { payAtPump, payInside, wallet, orderAhead },
  } = useConfiguration();

  const { isEmailCompleted, isPayAtPumpEnabled, isPayInsideEnabled } = useMemo(
    () => ({
      isEmailCompleted: hasEmail,
      isPayAtPumpEnabled: payAtPump.enabled,
      isPayInsideEnabled: payInside.enabled && wallet.enabled,
    }),
    [hasEmail, payAtPump.enabled, payInside.enabled, wallet.enabled],
  );

  const startPayAtPump = useStartPayAtPump({ isEmailCompleted });
  const startPayInside = useStartPayInside({ isEmailCompleted });
  const { pushToSurveys, pushToHome } = useGetNavigationActions();
  const handleOrderAheadNavigationClick = useOrderAheadNavigationOnClick();

  return useMemo(() => {
    const tabs: BottomNavigationTab[] = [
      {
        key: 'tabBar:homeButtonText',
        label: intl.formatMessage(messages['tabBar:homeButtonText']),
        Icon: TabBarHomeIcon,
        routerPath: AppRoutePath.Home,
        onClick: pushToHome,
      },
      {
        key: 'tabBar:surveysButtonText',
        label: intl.formatMessage(messages['tabBar:surveysButtonText']),
        Icon: SurveysTabBarIconWithBadgeController,
        onClick: pushToSurveys,
        routerPath: AppRoutePath.Surveys,
      },
    ];

    if (isPayAtPumpEnabled) {
      tabs.push({
        key: 'tabBar:payAtPumpButtonText',
        label: intl.formatMessage(messages['tabBar:payAtPumpButtonText']),
        Icon: TabBarPayAtPumpIcon,
        onClick: startPayAtPump,
        routerPath: AppRoutePath.PayAtPump,
      });
    }
    if (isPayInsideEnabled) {
      tabs.push({
        key: 'tabBar:payInsideButtonText',
        label: intl.formatMessage(messages['tabBar:payInsideButtonText']),
        Icon: QRCodeIcon,
        onClick: startPayInside,
        routerPath: AppRoutePath.PayInside,
      });
    }
    if (orderAhead.enabled) {
      tabs.push({
        key: 'tabBar:orderAheadButtonText',
        label: orderAhead.navbar.text,
        Icon: orderAheadIconMap[orderAhead.navbar.icon],
        onClick: handleOrderAheadNavigationClick,
        routerPath: AppRoutePath.OrderAhead,
      });
    }
    return { tabs };
  }, [
    intl,
    isPayAtPumpEnabled,
    isPayInsideEnabled,
    pushToHome,
    pushToSurveys,
    startPayAtPump,
    startPayInside,
    orderAhead,
    handleOrderAheadNavigationClick,
  ]);
};
